import Button from '@atlas/uicorn/lib/Control/Button';
import IconButton from '@atlas/uicorn/lib/Control/IconButton';
import Label from '@atlas/uicorn/lib/Display/Label';
import CrossIcon from '@atlas/uicorn/lib/Icon/CrossIcon';
import PlusThickIcon from '@atlas/uicorn/lib/Icon/PlusThickIcon';
import type { TExample } from '@atlas/uicorn/preview/modules';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import useRefCallback from 'use-ref-callback';

const LabelList = React.memo(function LabelList(props: TLabelListProps): React.ReactElement {
    const { addLabel, clearLabel, children, onAdd, onClear, readOnly, ...elementProps } = props;

    const onAddCallback = useRefCallback(onAdd);
    const onClearCallback = useRefCallback(onClear);

    const isListEmpty = useMemo(() => React.Children.count(children) === 0, [children]);

    return (
        <SLabelList {...elementProps}>
            {children}
            {onAddCallback &&
                (isListEmpty ? (
                    <Button onClick={onAddCallback} disabled={readOnly} type="plain" prefix={<SPlusThickIcon />}>
                        {addLabel ?? 'Add'}
                    </Button>
                ) : (
                    <SActionButton
                        icon={<PlusThickIcon />}
                        tooltip={addLabel ?? 'Add...'}
                        onClick={onAddCallback}
                        disabled={readOnly}
                    />
                ))}
            {onClearCallback && !isListEmpty && (
                <SActionButton
                    icon={<CrossIcon size={16} />}
                    tooltip={clearLabel ?? 'Clear'}
                    onClick={onClearCallback}
                    disabled={readOnly}
                />
            )}
        </SLabelList>
    );
});

export default LabelList;

// #region Styles

const SLabelList = styled.span`
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.6rem;
    max-width: 100%;
`;

export const SActionButton = styled(IconButton)`
    height: 2.2rem;
    width: 2.2rem;

    color: ${(p) => p.theme.color.grey8};
    background-color: ${(p) => p.theme.color.grey5};

    &:not(:disabled):hover {
        background-color: ${(p) => p.theme.color.grey6};
    }

    justify-content: center;
`;

const SPlusThickIcon = styled(PlusThickIcon)`
    color: ${(p) => p.theme.color.grey8};
`;

// #endregion

// #region Types

export type TLabelListProps = React.HTMLAttributes<HTMLDivElement> & {
    onAdd?: () => void;
    onClear?: () => void;
    readOnly?: boolean;
    addLabel?: string;
    clearLabel?: string;
    gap?: number;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        const [list, setList] = useState<number[]>(props?.initialList ?? []);

        useEffect(() => {
            if (!props.count) return;

            setList(Array.from({ length: props.count }, (_) => Math.random()));
        }, [props.count]);

        const handleAdd = useCallback(() => setList((v) => [...v, Math.random()]), []);
        const handleClear = useCallback(() => setList([]), []);

        const controlProps = useMemo(
            () =>
                props.enableControls
                    ? {
                          onAdd: handleAdd,
                          onClear: handleClear,
                          addLabel: 'Add a number',
                          clearLabel: 'Clear all numbers',
                      }
                    : {},
            [props.enableControls, handleAdd, handleClear],
        );

        return (
            <LabelList {...controlProps}>
                {list.map((v) => (
                    <Label color="turquoise" key={v}>
                        {v.toString()}
                    </Label>
                ))}
            </LabelList>
        );
    },
    controls: {
        count: { type: 'number', initial: 3 },
        enableControls: { type: 'boolean', initial: true },
    },
    handlers: {},
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('LabelList', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render initialList={[555, 777, 999]} />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
